import { useNotificationRoute } from 'app/features/notifications/NotificationRoutes';
import {
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useResetNotificationCounterMutation,
} from 'app/services/notifications';
import { INotification } from 'app/services/notifications/types';
import { BellIcon } from 'assets/icons';
import { USDate } from 'Components/Dates/USDate';
import Tooltip from 'Components/Tooltips';
import UserAvatar from 'Components/UserAvatar';
import UserRole from 'Components/UserRole';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

function BaseNotificationCard({
  value,

  onMarkAsRead,
}: {
  value: INotification;

  onMarkAsRead?: () => void;
}) {
  const { notification, created_at, read } = value;

  return (
    <div className="flex flex-wrap space-between items-center hover:bg-gray-300 py-4 px-2 rounded border-b border-gray-800">
      <div className="flex gap-2">
        <div className="relative self-start">
          <UserAvatar user={notification.actor} />
          <div className="bg-green rounded-full w-5 h-5 p-1 flex border border-white justify-center items-center  absolute -bottom-1 -right-1">
            <BellIcon className="text-white" />
          </div>
        </div>
        <div className="flex flex-col text-xs gap-1">
          <div className="flex items-center gap-1">
            <div className="text-gray-dark capitalize font-bold">
              {notification.actor.given_name} {notification.actor.family_name}
            </div>

            <UserRole roles={notification.actor.roles} />
          </div>

          <div className="text-gray-700">{notification.title}</div>
        </div>
      </div>

      <div className="ml-auto flex gap-2 py-3 self-start text-gray-light text-xs">
        <USDate date={created_at} format="long" />

        <div className="">
          {!read && (
            <Tooltip message="Mark As Read">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMarkAsRead && onMarkAsRead();
                }}
                className="flex rounded-full justify-center items-center p-1 hover:border hover:border-green"
              >
                <span className="rounded-full  w-2 h-2  bg-green"></span>
              </button>
            </Tooltip>
          )}
          {read && (
            <div className="flex rounded-full justify-center items-center p-1">
              <span className="rounded-full  w-2 h-2 border"></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function useMarkAsRead(value: INotification) {
  const [MarkAsRead] = useMarkNotificationAsReadMutation();
  const [ResetCounterTrigger] = useResetNotificationCounterMutation();
  const markAsRead = useCallback(() => {
    ResetCounterTrigger();
    if (value.read) return Promise.resolve(value);
    return MarkAsRead(value.id).unwrap();
  }, [value.read]);

  return markAsRead;
}

export default function NotifcationCard({
  value,
  onChange,
}: {
  value: INotification;
  onChange?: (v: INotification) => void;
}) {
  const markAsRead = useMarkAsRead(value);
  const navigateUrl = useNotificationRoute(value);

  return (
    <Link
      to={navigateUrl || '#'}
      onClick={() => markAsRead()}
      className={navigateUrl ? 'cursor-pointer' : 'cursor-default'}
    >
      <BaseNotificationCard
        value={value}
        onMarkAsRead={() => markAsRead().then(onChange)}
      />
    </Link>
  );
}
